import React,{useState,useMemo} from 'react';
import {motion} from 'framer-motion';

function App() {
  const [targetWords,setTargetWords]=useState(10000);
  const [minutesPerDay,setMinutesPerDay]=useState(15);
  const [daysPerWeek,setDaysPerWeek]=useState(6);
  const [writingPace,setWritingPace]=useState(200); // words per 15 minutes

  const timeline=useMemo(()=> {
    const wordsPerMinute=writingPace / 15;
    const weeklyWords=wordsPerMinute * minutesPerDay * daysPerWeek;
    if (weeklyWords===0) {
      return {weeks: '∞'};
    }
    const weeksToFinish=Math.round(targetWords / weeklyWords);
    return {weeks: weeksToFinish};
  },[targetWords,minutesPerDay,daysPerWeek,writingPace]);

  const wordOptions=[
    {value: 10000,label: '10,000'},
    {value: 15000,label: '15,000'},
    {value: 20000,label: '20,000'}
  ];

  const minuteOptions=[15,30,60,120]; // Added 120 minutes
  const dayOptions=[3,4,5,6]; // Added 3 days back

  // Check if we're in embed mode
  const isEmbed = typeof window !== 'undefined' && 
    (window.location.search.includes('embed=1') || 
     window.location.pathname.includes('embed') ||
     window !== window.parent);

  // Adjust padding for embed mode
  const containerPadding = isEmbed ? 'py-4 px-3' : 'py-8 sm:py-12';
  const maxContainerWidth = isEmbed ? 'max-w-full' : 'max-w-2xl';

  return (
    <div className={`${isEmbed ? '' : 'min-h-screen'} bg-gradient-to-br from-gray-50 to-white`}>
      <div className={`${maxContainerWidth} mx-auto ${containerPadding}`}>
        {/* Header */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6}}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center mb-3">
            <svg className={`${isEmbed ? 'w-8 h-8' : 'w-10 h-10'} text-teal-600 mr-2`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
            </svg>
            <h1 className={`${isEmbed ? 'text-xl' : 'text-3xl'} font-bold text-gray-900`}>
              Finish Your Book in Just 15-Minutes a Day
            </h1>
          </div>
          
          <p className={`${isEmbed ? 'text-base' : 'text-xl'} text-gray-700 mb-4 leading-relaxed`}>
            Give God a few focused minutes each day—and watch what He does with them. 
            Many of our members have finished their first drafts in about 90 days and 
            gone on to publish within six months.
          </p>
        </motion.div>

        {/* Input Form */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.2}}
          className="bg-white rounded-3xl shadow-xl p-4 sm:p-8 mb-6"
        >
          {/* Target Length */}
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <svg className="w-6 h-6 text-teal-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
              </svg>
              <h3 className={`${isEmbed ? 'text-lg' : 'text-xl'} font-semibold text-gray-900`}>Target Length for a Short Book</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
              {wordOptions.map((option)=> (
                <button
                  key={option.value}
                  onClick={()=> setTargetWords(option.value)}
                  className={`py-2 px-3 rounded-xl font-medium transition-all duration-200 text-sm sm:text-base ${
                    targetWords===option.value
                      ? 'bg-teal-600 text-white shadow-lg transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label} words
                </button>
              ))}
            </div>
          </div>

          {/* Minutes per Day */}
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <svg className="w-6 h-6 text-teal-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <h3 className={`${isEmbed ? 'text-lg' : 'text-xl'} font-semibold text-gray-900`}>Minutes per Day</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {minuteOptions.map((minutes)=> (
                <button
                  key={minutes}
                  onClick={()=> setMinutesPerDay(minutes)}
                  className={`py-2 px-3 rounded-xl font-medium transition-all duration-200 text-sm sm:text-base ${
                    minutesPerDay===minutes
                      ? 'bg-teal-600 text-white shadow-lg transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {minutes}
                </button>
              ))}
            </div>
          </div>

          {/* Days per Week */}
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <svg className="w-6 h-6 text-teal-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              <h3 className={`${isEmbed ? 'text-lg' : 'text-xl'} font-semibold text-gray-900`}>Days per Week</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {dayOptions.map((days)=> (
                <button
                  key={days}
                  onClick={()=> setDaysPerWeek(days)}
                  className={`py-2 px-3 rounded-xl font-medium transition-all duration-200 text-sm sm:text-base ${
                    daysPerWeek===days
                      ? 'bg-teal-600 text-white shadow-lg transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {days}
                </button>
              ))}
            </div>
          </div>

          {/* Writing Pace */}
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <svg className="w-6 h-6 text-teal-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
              </svg>
              <h3 className={`${isEmbed ? 'text-lg' : 'text-xl'} font-semibold text-gray-900`}>Writing Pace</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={()=> setWritingPace(200)}
                className={`p-3 rounded-xl text-left transition-all duration-200 ${
                  writingPace===200
                    ? 'bg-teal-600 text-white shadow-lg transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="font-semibold mb-1 text-sm sm:text-base">Slow & Thoughtful</div>
                <div className="text-xs sm:text-sm opacity-90">200 words / 15 minutes</div>
              </button>
              <button
                onClick={()=> setWritingPace(300)}
                className={`p-3 rounded-xl text-left transition-all duration-200 ${
                  writingPace===300
                    ? 'bg-teal-600 text-white shadow-lg transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="font-semibold mb-1 text-sm sm:text-base">Steady Flow</div>
                <div className="text-xs sm:text-sm opacity-90">300 words / 15 minutes</div>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Result - Always visible */}
        <motion.div
          initial={{opacity: 0,y: 20,scale: 0.95}}
          animate={{opacity: 1,y: 0,scale: 1}}
          transition={{duration: 0.5,delay: 0.4}}
          className="mb-6"
        >
          <div className="bg-teal-500 text-white rounded-3xl p-4 sm:p-8 shadow-2xl">
            <div className="text-center">
              <div className={`${isEmbed ? 'text-4xl' : 'text-6xl'} mb-3`}>✍️</div>
              <div className={`${isEmbed ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl'} font-bold mb-2 leading-tight`}>
                At your pace, you could finish a {targetWords.toLocaleString()}-word book in{' '}
                <span className="text-yellow-300">
                  {timeline.weeks} weeks
                </span>.
              </div>
            </div>
          </div>
          
          <p className={`text-center text-gray-600 mt-4 ${isEmbed ? 'text-sm' : 'text-lg'}`}>
            Small, faithful steps add up fast. Inside Christian Book Academy, 
            we'll help you stay consistent and cross that finish line.
          </p>
        </motion.div>

        {/* Embed Code - Only show when NOT in embed mode */}
        {!isEmbed && (
          <div className="mt-12 p-6 bg-gray-100 rounded-2xl">
            <h4 className="font-semibold text-gray-900 mb-3">Embed Code:</h4>
            <code className="text-sm text-gray-700 break-all">
              {`<iframe src="${window.location.origin}${window.location.pathname}?embed=1" width="100%" height="900" frameBorder="0" style="border-radius: 16px;box-shadow: 0 10px 30px rgba(0,0,0,0.1);"></iframe>`}
            </code>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;